// Native v4 UpdateAppButton — v3 src/lib/offline/UpdateAppButton.tsx: same
// show gate (mac 9.0.0 ≤ v < 12.0.0) and dispatch wires (auto-update with
// download fallback). Shared by AppNotice + UnsupportedVersionPage.
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useAppRegion, useEnabledFeatures } from '@/hooks/dataHelpers';
import {
  AppType,
  getAppType,
  getAppVersion,
  isNewAppVersion,
} from '@/lib/offline/OfflineAppHelper';
import { useOfflineAppStore } from '@/store/offlineAppStore';

import { dispatchToDownloadNativeMacApp, dispatchToUpdateNativeMacApp } from './updateAppDispatch';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface UpdateAppButtonProps {
  size?: 'default' | 'sm';
}

export const UpdateAppButton = ({ size = 'default' }: UpdateAppButtonProps) => {
  const { t } = useTranslation();
  const appType = getAppType();
  const appRegion = useAppRegion();
  const enabledFeatures = useEnabledFeatures();
  const { executeMethods } = useOfflineAppStore();
  const currentAppVersion = getAppVersion(appType);

  // v3 quirk kept: the on-click fail path re-derives args itself.
  const callbackForAutoUpdateFailOrCompletion = (
    cbAppType: AppType,
    cbAppRegion: string,
    cbExecuteMethods: any,
    cbEnabledFeatures: any
  ) => {
    dispatchToDownloadNativeMacApp(cbAppType, cbAppRegion, cbExecuteMethods, cbEnabledFeatures);
  };

  const shouldShowUpdateButton =
    appType === 'mac' &&
    isNewAppVersion(currentAppVersion, '9.0.0') &&
    !isNewAppVersion(currentAppVersion, '12.0.0');

  if (!shouldShowUpdateButton) return null;

  return (
    <Button
      size={size}
      onClick={() =>
        dispatchToUpdateNativeMacApp(
          'on_click',
          appType,
          appRegion,
          enabledFeatures,
          executeMethods,
          callbackForAutoUpdateFailOrCompletion
        )
      }
      data-testid="v4-update-app"
    >
      {t('CommonText.update_app')}
    </Button>
  );
};

/* eslint-enable @typescript-eslint/no-explicit-any */
